import React, { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * ดูรูปเต็มจอ — กดรูปในอัลบั้มแล้วเปิด เลื่อนซ้าย/ขวาด้วยปุ่มหรือลูกศร ปิดด้วย Esc / คลิกพื้นหลัง
 * images: [{ url, name }] (ไฟล์จาก Strapi) หรือ [{ src, name }] (URL เต็ม เช่น blob ของไฟล์ที่ยังไม่อัปโหลด)
 * index: รูปที่เปิดอยู่
 * วาดผ่าน portal ไปที่ body — เปิดจากใน drawer/modal ที่ใช้ transform แล้วจะได้ไม่โดนกรอบนั้นจำกัด
 */
export default function Lightbox({ images, index, onClose, onChange, lang }) {
  const total = images.length;
  const current = images[index];

  const prev = () => onChange((index - 1 + total) % total);
  const next = () => onChange((index + 1) % total);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, total]);

  if (!current) return null;

  const navBtn =
    "absolute top-1/2 -translate-y-1/2 w-[44px] h-[44px] rounded-full bg-white/15 hover:bg-white/30 text-white text-title leading-none flex items-center justify-center transition-colors";

  return createPortal(
    <div
      className="fixed inset-0 z-[300] bg-black/90 flex items-center justify-center select-none"
      onClick={(e) => {
        // ไม่ให้คลิกทะลุไปโดนการ์ด/แถวที่เปิด lightbox นี้ (React ส่ง event ตาม component tree แม้อยู่ใน portal)
        e.stopPropagation();
        onClose();
      }}
    >
      {/* แถบบน: ลำดับรูป + ปิด */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-[16px] py-[10px] text-white">
        <p className="text-body">
          {index + 1} / {total}
        </p>
        <button
          type="button"
          aria-label={lang === "en" ? "Close" : "ปิด"}
          onClick={onClose}
          className="w-[36px] h-[36px] rounded-[6px] bg-white/15 hover:bg-white/30 text-lead leading-none"
        >
          ×
        </button>
      </div>

      <img
        src={current.src || `${process.env.REACT_APP_API_URL}${current.url}`}
        alt={current.name || ""}
        className="max-w-[94vw] max-h-[86vh] object-contain rounded-[8px] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />

      {total > 1 && (
        <>
          <button
            type="button"
            aria-label={lang === "en" ? "Previous" : "รูปก่อนหน้า"}
            className={`${navBtn} left-[12px]`}
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
          >
            ‹
          </button>
          <button
            type="button"
            aria-label={lang === "en" ? "Next" : "รูปถัดไป"}
            className={`${navBtn} right-[12px]`}
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
          >
            ›
          </button>
        </>
      )}
    </div>,
    document.body
  );
}
